import React from 'react';
import PropTypes from 'prop-types';

import LoaderProgress from '../../../components/Loader/LoaderProgress/LoaderProgress.js';
import modelAPI from '../../../api/model';
import Typography from '@material-ui/core/Typography';

import { withStyles } from '@material-ui/core/styles';
import styles from './styles.js';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'flatpickr/dist/flatpickr.min.css';

class Form extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
    };
  }

  componentDidMount() {
    import(/* webpackChunkName: "Formio" */ 'formiojs').then(({ Formio }) => {
      this.setState({
        loading: false,
      });
      Formio.createForm(document.getElementById('formio'), this.props.form, {
        breadcrumbSettings: { clickable: false },
        // Disable the cancel button as there is no hook to change the
        // behaviour like the next and previous wizard buttons.
        buttonSettings: {
          showCancel: false,
        },
        i18n: {
          en: {
            previous: 'Back',
            submit: 'Submit',
          },
        },
      }).then((form) => {
        function setCustomValidation() {
          // For the max character length validation, we want to restrict the input to the character limit
          // rather than showing a validation message
          form.components.forEach((panelComponent) => {
            panelComponent.components.forEach((inputComponent) => {
              const maxLength = inputComponent.component.validate.maxLength;
              if (maxLength && inputComponent.refs.input.length > 0) {
                const element = inputComponent.refs.input[0];
                element.setAttribute('maxlength', maxLength);
              }
            });
          });
        }

        /**
         * Disable next/submit button if current page of form has invalid values.
         */
        function setNextButtonState() {
          const buttonType = form.currentNextPage > 0 ? 'next' : 'submit';
          const button = form.refs[`${form.wizardKey}-${buttonType}`];
          if (!form.allPages[form.page].checkValidity()) {
            button.setAttribute('disabled', 'disabled');
          } else {
            button.removeAttribute('disabled');
          }
        }

        function fixSelect() {
          // Style select boxes correctly
          const selectBoxes = document.getElementsByTagName('select');
          for (let selectBox of selectBoxes) {
            selectBox.classList.add('form-select');
          }

          // Remove autocompleters
          const autoCompleters = document.getElementsByClassName(
            'formio-select-autocomplete-input'
          );
          for (let autoCompleter of autoCompleters) {
            autoCompleter.style.display = 'none';
          }
        }

        function addButtons(form, cancelAction) {
          const wizardNav = document.getElementById(`${form.wizardKey}-nav`);

          // Add button container for default wizard navigation buttons.
          let rightButtonContainer = document.getElementsByClassName(
            'list-inline-item right-button-container'
          )[0];
          if (!rightButtonContainer) {
            rightButtonContainer = document.createElement('li');
            rightButtonContainer.classList.add('list-inline-item');
            rightButtonContainer.classList.add('right-button-container');
            wizardNav.prepend(rightButtonContainer);
          }

          const buttons = document.getElementsByClassName('btn-primary');

          let cancelButtonExists = false;
          for (let button of buttons) {
            if (button.innerHTML == 'Cancel') {
              cancelButtonExists = true;
              continue;
            }

            if (rightButtonContainer.contains(button)) {
              continue;
            }

            rightButtonContainer.append(button);
          }

          if (!cancelButtonExists) {
            // Add custom 'Cancel' button
            const buttonContainer = document.createElement('li');
            buttonContainer.classList.add('list-inline-item');
            const button = document.createElement('button');
            buttonContainer.prepend(button);
            wizardNav.prepend(buttonContainer);
            button.innerHTML = 'Cancel';
            button.classList.add('btn', 'btn-primary');
            button.addEventListener('click', () => {
              cancelAction();
            });
          }
        }

        /**
         * Show current form progress (currentPage/totalPAges)
         */
        function addCustomComponents(form, cancelAction) {
          // Page count
          const currentPage = form.page + 1;
          const totalPages = document.getElementById(`${form.wizardKey}-header`)
            .children[0].childElementCount;
          let pageCount = document.createElement('div');
          pageCount.classList.add('page-count');
          pageCount.textContent = `${currentPage}/${totalPages}`;
          const wizardNav = document.getElementById(`${form.wizardKey}-nav`);
          wizardNav.parentNode.append(document.createElement('hr'));
          wizardNav.parentNode.append(pageCount);

          addButtons(form, cancelAction);

          fixSelect();

          const monthSelectors = document.getElementsByName('month');
          for (let monthSelector of monthSelectors) {
            monthSelector.style.height = '50px';
          }

          /* Flatpickr (date/time picker) customization */
          form.components[form.page].components.forEach((component) => {
            if (component.type == 'datetime') {
              // @todo Find a hook (form ready or similar) instead of
              // setTimeout(). For now setTimeout ensures we are customizing
              // flatpickr after it has been already initialized.
              setTimeout(() => {
                const fp = document.querySelector(
                  `#${component.id}-${component.key}`
                )._flatpickr;
                fp.config.position = 'auto left';
                fp.config.disableMobile = true;
                fp.config.onOpen.push(function () {
                  // Ideally we would set the custom day labels using
                  // the locale config option, but this is not supported
                  // when the flatpickr component has already been initialized.
                  document
                    .querySelectorAll('.flatpickr-weekday')
                    .forEach((div) => {
                      switch (div.textContent.trim()) {
                        case 'Sun':
                        case 'Sat':
                          div.textContent = 'S';
                          break;
                        case 'Mon':
                          div.textContent = 'M';
                          break;
                        case 'Tue':
                        case 'Thu':
                          div.textContent = 'T';
                          break;
                        case 'Wed':
                          div.textContent = 'W';
                          break;
                        case 'Fri':
                          div.textContent = 'F';
                          break;
                      }
                    });
                  document
                    .querySelectorAll('.flatpickr-calendar')
                    .forEach((div) => {
                      div.classList.add(
                        document.getElementById('formio').parentElement
                          .className
                      );
                    });
                });
              }, 0);
            }
          });
        }

        /**
         * Add hr/divider above conditional fields.
         */
        function addTextAreaDividers() {
          for (let component of form.allPages[form.page].components) {
            if (
              component.component.conditional.show === true ||
              component.component.customConditional
            ) {
              const element = component.element;
              if (
                !element.classList.contains('formio-hidden') &&
                element.childNodes[0].className != 'hr'
              ) {
                const divider = document.createElement('div');
                divider.classList.add('hr');
                component.element.prepend(divider);
              }
            }
          }
        }

        // Prevent the submission from going to the form.io server.
        form.nosubmit = true;
        const { onComplete, onCancel, onLoading, formId } = this.props;

        addCustomComponents(form, onCancel);
        setCustomValidation();
        setNextButtonState();

        form.on('prevPage', function () {
          addCustomComponents(form, onCancel);
          setCustomValidation();
          addTextAreaDividers();
        });

        form.on('nextPage', function () {
          setNextButtonState();
          addCustomComponents(form, onCancel);
          setCustomValidation();
          addTextAreaDividers();
        });

        let revealed = [];

        form.on('change', function (changed) {
          fixSelect();
          setCustomValidation();
          addTextAreaDividers();

          setTimeout(() => {
            addButtons(form, onCancel);
            setNextButtonState();
          }, 0);

          // Scroll conditional fields into view when are first revealed (on mobile).
          for (let component of form.allPages[form.page].components) {
            const elementId = component.component.id;
            const element = document.getElementById(elementId);
            if (
              !revealed.includes(elementId) &&
              component.component.conditional.show === true &&
              !element.classList.contains('formio-hidden')
            ) {
              revealed.push(elementId);

              element.scrollIntoView();
            }
          }
        });

        // Triggered when they click the submit button.
        form.on('submit', function (submission) {
          submission.data.formId = formId;
          onLoading();
          modelAPI
            .addFormSubmission(submission.data)
            .then(() => {
              onComplete();
            })
            .catch(() => {
              console.log('error');
              //this.setState({ open: false, hasError: true });
            });
        });
      });
    });
  }

  render() {
    const { classes, form } = this.props;

    const { loading } = this.state;

    return (
      <div className={classes.root}>
        <LoaderProgress show={loading} />
        {loading && <Typography>Loading form...</Typography>}
        <div id="formio"></div>
      </div>
    );
  }
}

Form.propTypes = {
  classes: PropTypes.object.isRequired,
  onComplete: PropTypes.func.isRequired,
};

Form.defaultProps = {};

export default withStyles(styles)(Form);
